/**
 * OpsBadgesWidget · v2.7.65+E136
 * ══════════════════════════════════════════════════════════════════════
 * Compact card rendered on the Admin Console overview showing three
 * "nothing-slips-through" counts:
 *   1. Pending /drive-with-us applications
 *   2. Pending CCVAP voucher applications
 *   3. Orphaned contractor setup packets (no application linked)
 *
 * Each tile deep-links to the exact admin surface where the review
 * happens, so a fresh-eyes admin can go from dashboard to action in
 * ONE click.
 *
 * Polls every 60s. Non-privileged roles quietly get zeros — the
 * authorize check in the backend already returns 403 for those.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import adminApi from "@/lib/adminApi";
import {
  Ticket, ClipboardList, Inbox, ChevronRight, Loader2, RefreshCw,
} from "lucide-react";

const POLL_INTERVAL_MS = 60_000;

function Tile({ label, count, hint, deepLink, Icon, tint, testid }) {
  const alert = (count || 0) > 0;
  const tints = {
    fuchsia: { border: alert ? "border-fuchsia-400" : "border-fuchsia-800/40",  bg: alert ? "bg-fuchsia-500/15" : "bg-fuchsia-500/5",  txt: "text-fuchsia-300" },
    amber:   { border: alert ? "border-amber-400"   : "border-amber-800/40",    bg: alert ? "bg-amber-500/15"   : "bg-amber-500/5",    txt: "text-amber-300"   },
    rose:    { border: alert ? "border-rose-400"    : "border-rose-800/40",     bg: alert ? "bg-rose-500/15"    : "bg-rose-500/5",     txt: "text-rose-300"    },
  }[tint];
  return (
    <Link
      to={deepLink}
      data-testid={`ops-tile-${testid}`}
      className={`rounded-xl border-2 ${tints.border} ${tints.bg} p-3 flex items-center gap-3 hover:brightness-125 transition group`}
    >
      <div className={`rounded-lg bg-black/30 p-2 flex-shrink-0`}>
        <Icon className={`h-5 w-5 ${tints.txt}`}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-2xl font-black leading-none ${tints.txt}`} data-testid={`ops-tile-count-${testid}`}>
          {count ?? 0}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-300 font-black mt-1">
          {label}
        </div>
        {hint && <div className="text-[10px] text-zinc-500 mt-0.5">{hint}</div>}
      </div>
      <ChevronRight className="h-4 w-4 text-zinc-500 group-hover:text-zinc-200 flex-shrink-0"/>
    </Link>
  );
}

export default function OpsBadgesWidget() {
  const [badges, setBadges] = useState({
    pending_applications: 0,
    pending_ccvap: 0,
    orphan_contractor_setups: 0,
    as_of: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/applications/ops-badges");
      setBadges(data);
      setError(null);
    } catch (e) {
      // 403 = non-privileged admin role — silently hide by leaving zeros.
      if (e?.response?.status !== 403) {
        setError(e?.response?.data?.detail || "Failed to load ops badges");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    const id = setInterval(reload, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [reload]);

  const total = (badges.pending_applications || 0) + (badges.pending_ccvap || 0) + (badges.orphan_contractor_setups || 0);

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-4 space-y-3" data-testid="ops-badges-widget">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-black uppercase tracking-widest text-zinc-100">
            CCVAP · Onboarding Ops
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            {total === 0
              ? "All clear — nothing waiting on review."
              : `${total} item${total === 1 ? "" : "s"} waiting on review · updated every 60s`}
          </p>
        </div>
        <button
          onClick={reload}
          className="text-zinc-500 hover:text-zinc-200 p-1"
          data-testid="ops-badges-refresh"
          title="Refresh now"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <RefreshCw className="h-3.5 w-3.5"/>}
        </button>
      </div>
      {error && (
        <div className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-400/30 rounded px-2 py-1">
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Tile
          label="Pending Applications"
          count={badges.pending_applications}
          hint="Drive With Us submissions"
          Icon={Inbox}
          tint="fuchsia"
          deepLink="/admin#sub=users-applications"
          testid="pending-apps"
        />
        <Tile
          label="Pending CCVAP"
          count={badges.pending_ccvap}
          hint="Voucher rider applications"
          Icon={Ticket}
          tint="amber"
          deepLink="/admin/vouchers"
          testid="pending-ccvap"
        />
        <Tile
          label="Orphan Packets"
          count={badges.orphan_contractor_setups}
          hint="Unlinked contractor setups"
          Icon={ClipboardList}
          tint="rose"
          deepLink="/admin#sub=users-contractor-setups"
          testid="orphan-packets"
        />
      </div>
    </div>
  );
}
